import { useCallback, useEffect, useRef, useState } from 'react';

export const useEventPopover = (showTooltip: boolean, onOpenModal: () => void) => {
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const [isEventHovered, setIsEventHovered] = useState(false);
	const [, setIsHoveringPopover] = useState(false);
	const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);

	const handleContentClick = useCallback(() => {
		if (!showTooltip) {
			onOpenModal();
		} else {
			setIsPopoverOpen((prev) => !prev);
		}
	}, [showTooltip, onOpenModal]);

	const handleEventMouseEnter = useCallback(() => {
		setIsEventHovered(true);
		if (showTooltip && !isPopoverOpen) {
			setIsPopoverOpen(true);
		}
	}, [showTooltip, isPopoverOpen]);

	const handleEventMouseLeave = useCallback(() => {
		setIsEventHovered(false);
		if (isPopoverOpen) {
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHoveringPopover((hovering) => {
					if (!hovering) {
						setIsPopoverOpen(false);
					}
					return hovering;
				});
			}, 150); // small delay to trigger popover hover effect
		}
	}, [isPopoverOpen]);

	const handlePopoverClose = useCallback(() => {
		if (!isEventHovered) {
			setIsPopoverOpen(false);
			setIsHoveringPopover(false);
		}
	}, [isEventHovered]);

	const handlePopoverMouseEnter = useCallback(() => {
		setIsHoveringPopover(true);
	}, []);

	const handlePopoverMouseLeave = useCallback(() => {
		setIsHoveringPopover(false);
		setIsPopoverOpen(false);
	}, []);

	return {
		isPopoverOpen,
		handleContentClick,
		handleEventMouseEnter,
		handleEventMouseLeave,
		handlePopoverClose,
		handlePopoverMouseEnter,
		handlePopoverMouseLeave,
	};
};
